import { FC, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ITabs, MuiModal, MuiTabPanel, MuiTabs } from "../../../../components";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import { createProgramSiteCoreKeys } from "../../siteCore/SiteCoreKeys";

import { useGetProgramTemplateActivityById } from "../../../../api/services/programTemplate/programTemplateApis";
import {
  toggleLoader,
  toggleToast,
} from "../../../../redux/slices/appState/appStateSlice";
import { InformationBadge } from "../../../../common/informationBadge/informationBadge";
import { ViewActivityWrapper } from "./ViewActivity.style";
import { ViewActivityGeneral } from "./viewActivityTabs/viewActivityGeneral/ViewActivityGeneral";
import { ViewActivityDefault } from "./viewActivityTabs/viewActivityDefault/ViewActivityDefault";
import {
  getValueOrBlank,
  isNotEmpty,
  replaceHashWithValue,
} from "../../../../helpers/utils";
import { ProgramTemplateActivitiesRowValue } from "../../modelsAndSchemas/Models";
import { MODULE_PERMISSIONS } from "../../../../helpers/constants";
import { usePermissions } from "../../../../hooks/usePermissions";
import { ViewManageLocale } from "../../../../components/manageLocale/ViewManageLocale";
import { getLocalizedTopicForViewActivity } from "../../modelsAndSchemas/utils";
import { userPreferredLanguageSelector } from "../../../../redux/slices/appState/appStateSelector";

interface CreateActivityProps {
  show: boolean;
  handleCloseModal: () => void;
  pageId: string;
  selectedActivity: ProgramTemplateActivitiesRowValue;
  isViewTemplate?: boolean;
  editButtonClick?: () => void;
}

export const ViewActivity: FC<CreateActivityProps> = ({
  show,
  handleCloseModal,
  pageId,
  selectedActivity,
  isViewTemplate = false,
  editButtonClick,
}) => {
  const dispatch = useDispatch();
  const userSelectedLanguage = useSelector(userPreferredLanguageSelector);
  const [selectedTab, setSelectedTab] = useState(0);
  const { hasModulePermission } = usePermissions();

  const { commonLabels, fetchLabel } = useSiteCore();

  const [showManageLocale, setShowManageLocale] = useState(false);

  const handleShowManageLocale = () => {
    setShowManageLocale(true);
  };

  const handleCloseShowManageLocale = () => {
    setShowManageLocale(false);
  };

  const {
    data: activityData,
    isFetching,
    isError,
  } = useGetProgramTemplateActivityById(selectedActivity.id);

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible: isFetching,
        message: commonLabels.loadingPleaseWait,
      }),
    );
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [isFetching]);

  useEffect(() => {
    if (isError) {
      dispatch(
        toggleToast({
          open: true,
          message: commonLabels.someThingWentWrongPleaseTryAgainLater,
          type: "error",
        }),
      );
      dispatch(toggleLoader({ isVisible: false }));
    }
  }, [isError]);

  const tabIdGeneral = `${pageId}-tab1`;
  const tabIdActivity = `${pageId}-tab2`;

  const tabs: ITabs[] = [
    {
      id: tabIdGeneral,
      label: fetchLabel(
        createProgramSiteCoreKeys.createActivityKey,
        createProgramSiteCoreKeys.generalTab,
      ),
    },
    {
      id: tabIdActivity,
      label: fetchLabel(
        createProgramSiteCoreKeys.createActivityKey,
        createProgramSiteCoreKeys.defaultActivityValues,
      ),
    },
  ];

  const onTabChange = (event: React.ChangeEvent<{}>, value: number) => {
    setSelectedTab(value);
  };

  const title = () =>
    replaceHashWithValue(
      fetchLabel(
        createProgramSiteCoreKeys.createActivityKey,
        createProgramSiteCoreKeys.viewActivityPopupTitle,
      ),
      getValueOrBlank(activityData?.activityName),
    );

  const bannerInfoMessage = fetchLabel(
    createProgramSiteCoreKeys.createActivityKey,
    selectedActivity.programFrameworkId
      ? createProgramSiteCoreKeys.frameworkSpecificActivityNote
      : createProgramSiteCoreKeys.activityNote,
  );

  return (
    <MuiModal
      open={show}
      onClose={handleCloseModal}
      title={title()}
      primaryButtonTitle=""
      secondaryButtonTitle={commonLabels.close}
      onSecondaryButtonClick={handleCloseModal}
      tertiaryButtonTitle={
        !isViewTemplate &&
        hasModulePermission(MODULE_PERMISSIONS.EDIT_ACTIVITIES_AND_DELIVERABLES)
          ? fetchLabel(
              createProgramSiteCoreKeys.createActivityKey,
              createProgramSiteCoreKeys.editActivityButton,
            )
          : ""
      }
      onTertiaryButtonClick={editButtonClick}
      hasLargeContent
      stackButtonsOnMobile={true}
      className={showManageLocale ? "hide-modal" : ""}
    >
      <ViewActivityWrapper>
        <InformationBadge message={bannerInfoMessage} />

        <MuiTabs
          tabs={tabs}
          value={selectedTab}
          onChange={onTabChange}
          tabsTestIds={[tabIdGeneral, tabIdActivity]}
        >
          <MuiTabPanel
            panelId={`${pageId}-panel1`}
            value={selectedTab}
            index={0}
            ariaLabelledById={tabIdGeneral}
          >
            <ViewActivityGeneral
              activityData={activityData}
              frameworkName={
                isNotEmpty(selectedActivity.programFrameworkId)
                  ? (selectedActivity.programFramework ?? "")
                  : ""
              }
              onViewManageLocal={handleShowManageLocale}
            />
          </MuiTabPanel>
          <MuiTabPanel
            panelId={`${pageId}-panel2`}
            value={selectedTab}
            index={1}
            ariaLabelledById={tabIdActivity}
          >
            <ViewActivityDefault activityData={activityData} />
          </MuiTabPanel>
        </MuiTabs>
        {showManageLocale && (
          <ViewManageLocale
            show={showManageLocale}
            handleCloseModal={handleCloseShowManageLocale}
            localeLanguages={getLocalizedTopicForViewActivity(
              activityData,
              userSelectedLanguage,
            )}
            title={title()}
            bannerInfoMessage={bannerInfoMessage}
          />
        )}
      </ViewActivityWrapper>
    </MuiModal>
  );
};
